import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, ArrowLeft, ArrowRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { Reveal } from "@/components/Reveal";
import { Lightbox } from "@/components/Lightbox";
const VideoCard = ({ v, i }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <Reveal delay={(i % 2) * 0.1}>
      <div className="group relative overflow-hidden aspect-video bg-ink2" data-testid={`reel-${i}`}>
        {playing ? (
          <video src={v.src} controls autoPlay className="w-full h-full object-cover" />
        ) : (
          <button onClick={() => setPlaying(true)} data-testid={`reel-play-${i}`} className="w-full h-full block relative">
            <img src={v.poster} alt={v.title} className="w-full h-full object-cover transition-transform duration-[1.3s] group-hover:scale-105" />
            <div className="absolute inset-0 bg-ink/40 group-hover:bg-ink/25 transition-colors duration-500 flex items-center justify-center">
              <span className="w-20 h-20 rounded-full border border-cream/60 flex items-center justify-center backdrop-blur-sm group-hover:bg-cream group-hover:text-ink text-cream transition-colors duration-500">
                <Play size={26} className="ml-1" fill="currentColor" />
              </span>
            </div>
            <span className="absolute bottom-5 left-5 font-serif text-2xl text-cream">{v.title}</span>
          </button>
        )}
      </div>
    </Reveal>
  );
};

const CategoryGallery = () => {
  const { categories } = useContent();
  const { slug } = useParams();
  const cat = categories.find((c) => c.slug === slug);
  const [lb, setLb] = useState(null);

  const idx = categories.findIndex((c) => c.slug === slug);
  const next = categories[(idx + 1) % categories.length];
  const prev = categories[(idx - 1 + categories.length) % categories.length];

  if (!cat) return <div className="pt-48 text-center font-serif text-4xl">Gallery not found.</div>;

  const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7", "md:col-span-6", "md:col-span-6"];

  return (
    <div data-testid="category-gallery" className="pt-36 md:pt-48 pb-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Reveal>
          <Link to="/portfolio" data-testid="back-to-portfolio" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted2 hover:text-gold transition-colors mb-10">
            <ArrowLeft size={14} /> All Galleries
          </Link>
          <p className="text-[0.65rem] uppercase tracking-[0.4em] text-gold mb-4">{String(idx + 1).padStart(2, "0")} — Gallery</p>
          <h1 className="font-serif text-5xl sm:text-7xl md:text-8xl font-light tracking-tight leading-none">{cat.title}</h1>
          <p className="mt-6 max-w-xl text-muted2 leading-relaxed">{cat.blurb}</p>
        </Reveal>

        {cat.isVideo ? (
          <div className="mt-16 grid md:grid-cols-2 gap-6">
            {cat.videos.map((v, i) => <VideoCard key={i} v={v} i={i} />)}
          </div>
        ) : (
          <div className="mt-16 grid md:grid-cols-12 gap-4 md:gap-6">
            {cat.images.map((img, i) => (
              <Reveal key={i} delay={(i % 3) * 0.08} className={spans[i % spans.length]}>
                <button
                  onClick={() => setLb(i)}
                  data-testid={`gallery-img-${i}`}
                  className={`group relative block w-full overflow-hidden ${i % 4 === 0 || i % 4 === 3 ? "aspect-[4/3]" : "aspect-[3/4]"}`}
                >
                  <img src={img} alt={`${cat.title} ${i + 1}`} className="w-full h-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-110" />
                  <div className="absolute inset-0 bg-ink/10 group-hover:bg-ink/30 transition-colors duration-500" />
                  <span className="absolute top-4 left-4 text-[0.6rem] uppercase tracking-[0.3em] text-cream/0 group-hover:text-cream transition-colors duration-500">View</span>
                </button>
              </Reveal>
            ))}
          </div>
        )}

        <div className="mt-28 pt-10 border-t border-white/10 flex justify-between">
          <Link to={`/portfolio/${prev.slug}`} data-testid="gallery-prev" className="group">
            <span className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted2 mb-2"><ArrowLeft size={14} /> Previous</span>
            <span className="font-serif text-2xl md:text-4xl font-light group-hover:text-gold transition-colors">{prev.title}</span>
          </Link>
          <Link to={`/portfolio/${next.slug}`} data-testid="gallery-next" className="group text-right">
            <span className="flex items-center justify-end gap-2 text-xs uppercase tracking-[0.25em] text-muted2 mb-2">Next <ArrowRight size={14} /></span>
            <span className="font-serif text-2xl md:text-4xl font-light group-hover:text-gold transition-colors">{next.title}</span>
          </Link>
        </div>
      </div>

      {lb !== null && !cat.isVideo && (
        <Lightbox images={cat.images} index={lb} onClose={() => setLb(null)} onNav={() => {}} />
      )}
    </div>
  );
};

export default CategoryGallery;
